import * as React from 'react';
import { Field } from 'redux-form';

import type { DivisionViewModel, GroupedDivisions } from 'ab-viewModels/division.viewModel';
import type { EmployeeOptionVM } from 'ab-viewModels/employee/option.viewModel';
import type JobStatusVM from 'ab-viewModels/jobStatus/option.viewModel';
import type LocationVM from 'ab-viewModels/location/option.viewModel';

import type { PriorityItem } from '@acceligentllc/shared/enums/priority';
import { PriorityList } from '@acceligentllc/shared/enums/priority';

import * as ColorUtils from 'af-utils/color.util';

import { SegmentLabel } from '@acceligentllc/storybook';

import Checkbox from 'af-fields/Checkbox';
import Dropdown from 'af-fields/Dropdown';
import Input from 'af-fields/Input';

import PriorityLabel from 'af-components/PriorityLabel';

import WorkRequestUpsertFM from '../../formModel';
import { formSectionClass, formSectionClassRow, formSectionClassColumn2, formSectionClassColumn4, formSectionDropdownItem, formSectionDropdownItemMain, formSectionDropdownItemSecondary } from '../../helpers';
import styles from '../../styles.module.scss';

type Props = {
	findJobStatuses: () => Promise<JobStatusVM[]>;
	findAllProjectManagers: () => Promise<EmployeeOptionVM[]>;
	findAllLocations: () => Promise<LocationVM[]>;
	findAllDivisions: () => Promise<GroupedDivisions>;
};

const filterOption = (option: EmployeeOptionVM, rawInput: string) => {
	const { firstName, lastName, uniqueId } = option;
	return `${firstName} ${lastName} ${uniqueId}`.toLowerCase().includes(rawInput.toLowerCase());
};

const renderJobStatus = (option: JobStatusVM) =>
	<div className={formSectionDropdownItem} key={option.id}>
		<div className={[formSectionDropdownItemMain, ColorUtils.getColorTextClass(option.color)].join(' ')}>
			{option.name}
		</div>
		<div className={formSectionDropdownItemSecondary}>
			{option.description}
		</div>
	</div>;

const renderSelectedJobStatus = (option: JobStatusVM) =>
	<div className={formSectionDropdownItem} key={option.id}>
		<div className={[formSectionDropdownItemMain, ColorUtils.getColorTextClass(option.color)].join(' ')}>
			{option.name}
		</div>
	</div>;

const renderEmployeeOption = (option: EmployeeOptionVM) =>
	<div className={formSectionDropdownItem} key={option.id}>
		<div className={formSectionDropdownItemMain}>
			{`${option.firstName} ${option.lastName} (${option.uniqueId})`}
		</div>
	</div>;

const renderSelectedProjectManager = (option: EmployeeOptionVM) =>
	<div className={formSectionDropdownItem} key={option.id}>
		<div className={formSectionDropdownItemMain}>
			{`${option.firstName} ${option.lastName} (${option.uniqueId})`}
		</div>
	</div>;

const renderOffice = (option: LocationVM) => <div key={option.id}>{option.nickname}</div>;

const renderDivision = (option: DivisionViewModel) => <div key={option.id}>{option.name}</div>;

const JobSummarySection: React.FC<Props> = ({ findJobStatuses, findAllProjectManagers, findAllLocations, findAllDivisions }) => {
	const renderPriorityItem = React.useCallback((option: PriorityItem) => <PriorityLabel priority={option.id} />, []);
	const [jobStatuses, setJobStatuses] = React.useState<JobStatusVM[]>([]);
	const [projectManagers, setProjectManagers] = React.useState<EmployeeOptionVM[]>([]);
	const [offices, setOffices] = React.useState<LocationVM[]>([]);
	const [divisions, setDivisions] = React.useState<DivisionViewModel[]>([]);

	const lazyLoadJobStatuses = React.useCallback(async (isLazyLoaded: boolean) => {
		if (!isLazyLoaded) {
			const fetchedJobStatuses = await findJobStatuses();
			setJobStatuses(fetchedJobStatuses ?? []);
		}
	}, [findJobStatuses]);

	const lazyLoadOffices = React.useCallback(async (isLazyLoaded: boolean) => {
		if (!isLazyLoaded) {
			const fetchedOffices = await findAllLocations();
			setOffices(fetchedOffices ?? []);
		}
	}, [findAllLocations]);

	const lazyLoadProjectManagers = React.useCallback(async (isLazyLoaded: boolean) => {
		if (!isLazyLoaded) {
			const fetchedProjectManagers = await findAllProjectManagers();
			setProjectManagers(fetchedProjectManagers ?? []);
		}
	}, [findAllProjectManagers]);

	const lazyLoadDivisions = React.useCallback(async (isLazyLoaded: boolean) => {
		if (!isLazyLoaded) {
			const fetchedDivisions = await findAllDivisions();
			setDivisions(Object.values<DivisionViewModel[]>(fetchedDivisions).flat());
		}
	}, [findAllDivisions]);

	const checkboxStyle = React.useMemo(() => [formSectionClassColumn2, styles['work-request-form__tab-content__form-section__row__centered-column']].join(' '), []);

	return (
		<div className={formSectionClass}>
			<SegmentLabel
				label="Job Summary"
				tooltip="A summary of the key details associated with this job."
			/>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						id="jobCode"
						isRequired={true}
						label="Work Request ID"
						name={WorkRequestUpsertFM.getAttributeName('jobCode')}
						placeholder="Enter Work Request ID"
						type="text"
					/>
				</div>
				<div className={formSectionClassColumn4}>
					<Field
						component={Input}
						id="title"
						isRequired={true}
						label="Job Title"
						name={WorkRequestUpsertFM.getAttributeName('title')}
						placeholder="Enter Job Title"
						type="text"
					/>
				</div>
				<div className={checkboxStyle}>
					<Field
						component={Checkbox}
						id="isInternal"
						label="Internal Job"
						name={WorkRequestUpsertFM.getAttributeName('isInternal')}
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						id="customerCompany"
						isRequired={true}
						label="Customer Company"
						name={WorkRequestUpsertFM.getAttributeName('customerCompanyName')}
						placeholder="Enter Customer Company"
						type="text"
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Input}
						id="customerReferencePO"
						label="Customer Reference/PO"
						name={WorkRequestUpsertFM.getAttributeName('customerReferencePO')}
						placeholder="Enter Customer Reference/PO"
						type="text"
					/>
				</div>
				<div className={checkboxStyle}>
					<Field
						component={Checkbox}
						id="isEmergencyJob"
						label="Emergency Job"
						name={WorkRequestUpsertFM.getAttributeName('isEmergencyJob')}
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn2}>
					<Field
						component={Dropdown}
						id="priority"
						label="Priority *"
						name="priority"
						options={PriorityList}
						renderMenuItem={renderPriorityItem}
						valueKey="id"
						withCaret={true}
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Dropdown}
						hasBlankOption={true}
						id="jobStatusId"
						label="Job Status"
						name={WorkRequestUpsertFM.getAttributeName('jobStatusId')}
						onLazyLoad={lazyLoadJobStatuses}
						options={jobStatuses}
						placeholder="Select Status"
						propName="jobStatus"
						renderMenuItem={renderJobStatus}
						renderSelected={renderSelectedJobStatus}
						valueKey="id"
						withCaret={true}
					/>
				</div>
			</div>
			<div className={formSectionClassRow}>
				<div className={formSectionClassColumn2}>
					<Field
						component={Dropdown}
						filterable={true}
						filterBy={filterOption}
						id="projectManagerId"
						label="Project Manager"
						name="projectManagerId"
						onLazyLoad={lazyLoadProjectManagers}
						options={projectManagers}
						placeholder="Select Project Manager"
						propName="projectManager"
						renderMenuItem={renderEmployeeOption}
						renderSelected={renderSelectedProjectManager}
						valueKey="id"
						withCaret={true}
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Dropdown}
						id="officeId"
						label="Office *"
						name="officeId"
						onLazyLoad={lazyLoadOffices}
						options={offices}
						placeholder="Select Office"
						propName="office"
						renderMenuItem={renderOffice}
						valueKey="id"
						withCaret={true}
					/>
				</div>
				<div className={formSectionClassColumn2}>
					<Field
						component={Dropdown}
						hasBlankOption={true}
						id="divisionId"
						label="Division"
						name="divisionId"
						onLazyLoad={lazyLoadDivisions}
						options={divisions}
						placeholder="Select Division"
						propName="division"
						renderMenuItem={renderDivision}
						valueKey="id"
						withCaret={true}
					/>
				</div>
			</div>
		</div>
	);
};

export default React.memo(JobSummarySection);
